'use client';

import {
  type Permission,
  PermissionCategory,
  PermissionType,
  type Role,
} from '@repo/api-gw-sdk';
import { useMemo, type PropsWithChildren } from 'react';

import { rolesComparer } from '@/configs/roles';
import { RolesContext } from '@/contexts/rolesContext';

import { Permissions, type PermissionDescriptor } from './permissions';
import { useFeatureFlags } from './useFeatureFlags';
import { useNonProjectDal } from './useNonProjectDal';
import { useUser } from './useUser';

const isAuthorized = (scope: string, roleScopes: string[]) => {
  const [permission, action] = scope.split(':');
  const optionalScopes = [
    scope,
    `${permission}:all`,
    `admin:${action}`,
    'admin:all',
  ];
  return optionalScopes.some((x) => roleScopes.includes(x));
};

export const RolesProvider = ({ children }: PropsWithChildren) => {
  const { nonProjectDal: dal } = useNonProjectDal();
  const { ZeroEtlUiEnabled } = useFeatureFlags();

  const { user } = useUser();

  const { data: rolesBody, mutate: reloadRoles } = dal.roles.useList();
  const { data: permissionsBody } = dal.roles.useListPermissions();

  const roles = useMemo(
    () => (rolesBody?.roles ?? []).sort(rolesComparer),
    [rolesBody]
  );
  const rolesMap = useMemo(
    () =>
      Object.assign({}, ...roles.map((role: Role) => ({ [role.id]: role }))),
    [roles]
  );

  const permissions = useMemo(
    () =>
      (permissionsBody?.permissions ?? []).reduce<
        Record<string, PermissionDescriptor>
      >((acc: Record<string, PermissionDescriptor>, permission: Permission) => {
        if (
          !ZeroEtlUiEnabled &&
          permission.permissionType === PermissionType.ZeroEtlPoliciesManage
        ) {
          return acc;
        }

        if (Permissions[permission.permissionType]) {
          acc[permission.permissionType] = {
            ...Permissions[permission.permissionType],
            ...permission,
          };
        }

        return acc;
      }, {}),
    [ZeroEtlUiEnabled, permissionsBody]
  );

  return (
    <RolesContext.Provider
      value={{
        roles,
        rolesMap,
        permissions,
        isGlobalRole: (role) =>
          !!role?.permissionGrants.some(
            (grant) =>
              permissions[grant.permission]?.category ===
              PermissionCategory.PERMISSION_CATEGORY_GLOBAL_MANAGEMENT
          ),
        isAuthorized: (scope, roleOverride) =>
          isAuthorized(
            scope,
            roleOverride?.scopes || user?.selectedRole.scopes || []
          ),
        isAuthorizedToGroup: (group, roleOverride) => {
          const groupPermissionTypes = Object.values(permissions)
            .filter((permission) => permission.group === group)
            .map((permission) => permission.permissionType);

          const grants =
            roleOverride?.permissionGrants ||
            user?.selectedRole.permissionGrants ||
            [];

          return grants.some((grant) =>
            groupPermissionTypes.includes(grant.permission)
          );
        },
        isAuthorizedResource: (scope, resource) => {
          if (!resource) {
            return false;
          }

          if (!resource.metadata?.disallowedScopes) {
            return isAuthorized(scope, user?.selectedRole.scopes || []);
          }

          if (resource.metadata.disallowedScopes.includes(scope)) {
            return false;
          }

          return isAuthorized(scope, user?.selectedRole.scopes || []);
        },
        refreshRoles: async () => {
          await reloadRoles();
        },
      }}
    >
      {children}
    </RolesContext.Provider>
  );
};
